import '../components/Map/controls/measuretools/style.scss';
import '@fortawesome/fontawesome-free/css/all.css';

import * as condition from 'ol/events/condition';

import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style';
import {
  Draw,
  Interaction,
  Modify,
  defaults as defaultInteractions,
} from 'ol/interaction';
import { LineString, MultiPoint, Polygon } from 'ol/geom';
import { Map, MapBrowserEvent } from 'ol';
import { getArea, getLength } from 'ol/sphere';

import { Coordinate } from 'ol/coordinate';
import { DrawEvent } from 'ol/interaction/Draw';
import { FeatureLike } from 'ol/Feature';
import { ModifyEvent } from 'ol/interaction/Modify';
import Overlay from 'ol/Overlay';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import OLMap from 'ol/Map';
import { action, makeObservable, observable } from 'mobx';

const GEOMETRY_TYPE = {
  LINE_STRING: 'LineString',
  POLYGON: 'Polygon',
} as const;

const OVERLAY_POSITIONING = {
  CENTER_CENTER: 'center-center',
  BOTTOM_CENTER: 'bottom-center',
} as const;

interface Options {
  tools?: String[];
  editable?: Boolean;
  displaySegments?: Boolean;
  showHelpTooltip?: Boolean;
  className?: string;
  buttonClassName?: string;
  buttonTitle?: string;
  toolsText?: HTMLElement[];
  toolsTitle?: String[];
  vectorStyle?: Style;
  drawStyle?: Style;
  modifyOptions?: Object;
  showProjMeasurement?: Boolean;
}

export enum ToolsType {
  area = 'area',
  distance = 'distance',
}

/**
 * @classdesc Measure widget with feature of calculating drawed distance or area
 */
export class MeasureTools {
  private options: any;
  private measureTooltipElement: HTMLDivElement;
  private measureTooltip: Overlay | undefined;
  private segmentTooltips: Overlay[] | undefined;
  private mapInteractions: Interaction[] | undefined;
  private feature: FeatureLike | undefined;
  private helpTooltipElement: HTMLElement;
  private helpTooltip: Overlay | undefined;
  private _map: OLMap;
  @observable activeTool: { type: ToolsType; disposer: VoidFunction } | null =
    null;

  constructor(map: OLMap, options?: Options) {
    this._map = map;
    const element = document.createElement('div'),
      className = options?.className || 'ol-measure-tools';

    element.className = `ol-unselectable ol-control ${className}`;

    this.helpTooltipElement = document.createElement('div');
    this.options = {
      tools: Array.isArray(options?.tools)
        ? options?.tools
        : ['distance', 'area'],
      editable:
        typeof options?.editable === 'boolean' ? options?.editable : true,
      displaySegments:
        typeof options?.displaySegments === 'boolean'
          ? options?.displaySegments
          : false,
      showHelpTooltip:
        typeof options?.showHelpTooltip === 'boolean'
          ? options?.showHelpTooltip
          : false,
      buttonClassName: options?.buttonClassName
        ? options.buttonClassName
        : 'widget-button',
      buttonTitle: 'Инструменты измерения',
      toolsText: [
        this._createIcon('fas fa-ruler'),
        this._createIcon('fas fa-draw-polygon'),
      ],
      toolsTitle: ['Дистанция', 'Площадь'],
      vectorStyle: options?.vectorStyle ? options.vectorStyle : null,
      drawStyle: options?.drawStyle ? options?.drawStyle : null,
      modifyOptions: options?.modifyOptions ? options.modifyOptions : {},
      showProjMeasurement:
        typeof options?.showProjMeasurement === 'boolean'
          ? options?.showProjMeasurement
          : true,
    };

    this.measureTooltipElement = document.createElement('div');
    makeObservable(this);
  }

  _createIcon(className: string) {
    const el = document.createElement('i');
    el.className = className;

    return el;
  }

  @action enableTool(tool: ToolsType) {
    if (this.activeTool) {
      this.activeTool.disposer();
    }

    const toolDisposer = this.addInteraction(tool);

    this.activeTool = { type: tool, disposer: toolDisposer };
  }

  @action disableTool() {
    this.activeTool?.disposer();
    this.activeTool = null;
  }

  addInteraction(type: ToolsType) {
    enum types {
      'distance' = 'LINE_STRING',
      'area' = 'POLYGON',
    }

    const pointerMoveHandler = (e: MapBrowserEvent) => {
      if (e.dragging) return;

      let helpMsg = 'Кликните, чтобы начать рисовать';
      const continuePolygonMsg = 'Кликните, чтобы продолжить рисовать полигон';
      const continueLineMsg = 'Кликните, чтобы продолжить рисовать линию';

      if (this.feature) {
        const geom = this.feature.getGeometry();
        if (geom instanceof Polygon) {
          helpMsg = continuePolygonMsg;
        } else if (geom instanceof LineString) {
          helpMsg = continueLineMsg;
        }
      }

      this.helpTooltipElement.innerHTML = helpMsg;
      this.helpTooltip?.setPosition(e.coordinate);

      this.helpTooltipElement.classList.remove('hidden');
    };
    if (this.options.showHelpTooltip)
      this._map.on('pointermove', pointerMoveHandler);

    if (!this.options.vectorStyle)
      (this.options.vectorStyle =
        /* We are using two different styles for the polygons:
         * - The first style is for the polygons themselves.
         * - The second style is to draw the vertices of the polygons.
         *   In a custom `geometry` function the vertices of a polygon are
         *   returned as `MultiPoint` geometry, which will be used to render
         *   the style.
         */
        new Style({
          stroke: new Stroke({
            color: 'blue',
            width: 3,
          }),
          fill: new Fill({
            color: 'rgba(0, 0, 255, 0.1)',
          }),
        })),
        new Style({
          image: new CircleStyle({
            radius: 5,
            fill: new Fill({
              color: 'orange',
            }),
          }),
          geometry: (feature) => {
            const geom = feature.getGeometry();
            if (geom instanceof LineString) {
              return new MultiPoint(geom.getCoordinates());
            } else if (geom instanceof Polygon) {
              return new MultiPoint(geom.getFlatCoordinates());
            } else {
              return;
            }
          },
        });

    const source = new VectorSource();
    const vector = new VectorLayer({
      source,
      style: this.options.vectorStyle,
    });
    this._map.addLayer(vector);
    vector.setZIndex(Infinity);

    if (!this.options.drawStyle)
      this.options.drawStyle = new Style({
        fill: new Fill({
          color: 'rgba(0, 0, 255, 0.1)',
        }),
        stroke: new Stroke({
          color: 'blue',
          lineDash: [10, 10],
          width: 2,
        }),
        image: new CircleStyle({
          radius: 5,
          stroke: new Stroke({
            color: 'rgba(0, 0, 0, 0.7)',
          }),
          fill: new Fill({
            color: 'rgba(255, 255, 255, 0.2)',
          }),
        }),
      });

    const measureType = types[type];
    const draw = new Draw({
      source,
      type: GEOMETRY_TYPE[measureType],
      style: this.options.drawStyle,
      stopClick: true,
    });

    const newInteractions = defaultInteractions({
      doubleClickZoom: false,
      altShiftDragRotate: false,
      pinchRotate: false,
    });
    newInteractions.push(draw);

    this.mapInteractions = [...this._map.getInteractions().getArray()];
    const interactions = this._map.getInteractions();
    interactions.clear();
    interactions.extend(newInteractions.getArray());

    const modify = new Modify({
      source,
      deleteCondition: condition.click,
      pixelTolerance: 30,
      ...this.options.modifyOptions,
    });

    this._map.addOverlay(this.createHelpTooltip());
    this._map.addOverlay(this.createMeasureTooltip());

    draw.on('drawstart', (e: DrawEvent) => {
      let tooltipCoords: Coordinate;

      this.feature = e.feature;
      this.feature?.getGeometry()?.on('change', (e) => {
        const geom = e.target;
        const projection = this._map.getView().getProjection().getCode();
        if (geom instanceof LineString) {
          if (this.options.displaySegments)
            this.createSegmentTooltips(geom, this._map);
          (this.measureTooltipElement.firstChild as HTMLElement).innerHTML =
            this.formatLength(geom, projection);
          tooltipCoords = geom.getLastCoordinate();
        } else if (geom instanceof Polygon) {
          (this.measureTooltipElement.firstChild as HTMLElement).innerHTML =
            this.formatArea(geom, projection);
          tooltipCoords = geom.getInteriorPoint().getCoordinates();
        }
        if (this.measureTooltip) {
          this.measureTooltip.setPosition(tooltipCoords);
        }
      });
    });

    draw.on('drawend', () => {
      this.feature = undefined;
      if (this.options.showHelpTooltip)
        this._map.un('pointermove', pointerMoveHandler);

      if (this.helpTooltip) {
        this._map.removeOverlay(this.helpTooltip);
      }

      if (this.mapInteractions) {
        const interactions = this._map.getInteractions();
        interactions.clear();
        interactions.extend(this.mapInteractions);
      }

      if (this.options.editable) this._map.addInteraction(modify);

      this.measureTooltipElement.className = 'tooltip tooltip-static';
      if (this.measureTooltip) {
        this.measureTooltip.setOffset([0, -15]);
      }
      this.addTooltipClose();
    });

    modify.on('modifystart', (e: ModifyEvent) => {
      const feature = e.features.getArray()[0];
      feature?.getGeometry()?.on('change', (e) => {
        const geom = e.target;
        const projection = this._map.getView().getProjection().getCode();

        if (geom instanceof LineString) {
          (this.measureTooltipElement.firstChild as HTMLElement).innerHTML =
            this.formatLength(geom, projection);
        } else if (geom instanceof Polygon) {
          (this.measureTooltipElement.firstChild as HTMLElement).innerHTML =
            this.formatArea(geom, projection);
        }
      });
    });

    return () => {
      this.measureTooltipElement.parentNode?.removeChild(
        this.measureTooltipElement,
      );

      if (this.mapInteractions) {
        const interactions = this._map.getInteractions();
        interactions.clear();
        interactions.extend(this.mapInteractions);
      }

      this._map.removeLayer(vector);
      if (this.helpTooltip) {
        this._map.removeOverlay(this.helpTooltip);
      }
      if (this.measureTooltip) {
        this._map.removeOverlay(this.measureTooltip);
      }
      if (this.segmentTooltips) {
        this.segmentTooltips.map((x: Overlay) => this._map.removeOverlay(x));
      }
    };
  }

  /**
   * Create help tooltip element
   */
  createHelpTooltip() {
    if (this.helpTooltipElement) {
      this.helpTooltipElement.parentNode?.removeChild(this.helpTooltipElement);
    }
    this.helpTooltipElement = document.createElement('div');
    this.helpTooltipElement.className = 'tooltip hidden';

    this.helpTooltip = new Overlay({
      element: this.helpTooltipElement,
      offset: [15, 0],
      positioning: OVERLAY_POSITIONING.CENTER_CENTER,
    });

    return this.helpTooltip;
  }

  /**
   * Create measure tooltip element
   */
  createMeasureTooltip() {
    this.measureTooltipElement?.parentNode?.removeChild(
      this.measureTooltipElement,
    );
    this.measureTooltipElement = document.createElement('div');
    this.measureTooltipElement.className = 'tooltip';

    this.measureTooltipElement.appendChild(document.createElement('span'));

    this.measureTooltip = new Overlay({
      element: this.measureTooltipElement,
      offset: [0, -15],
      positioning: OVERLAY_POSITIONING.BOTTOM_CENTER,
    });

    return this.measureTooltip;
  }

  /**
   * Create segment measure tooltips
   */
  createSegmentTooltips(geom: LineString, map: Map) {
    if (this.segmentTooltips)
      this.segmentTooltips.map((x: Overlay) => map.removeOverlay(x));
    this.segmentTooltips = [];

    const coords = geom.getCoordinates();

    for (let i = 1; i < coords.length - 1; i++) {
      const segmentTooltipElement = document.createElement('div');
      segmentTooltipElement.className = 'tooltip';

      const length = this.formatLength(new LineString(coords.slice(0, i + 1)));
      segmentTooltipElement.innerHTML = length;

      const segmentTooltip = new Overlay({
        element: segmentTooltipElement,
        offset: [0, -15],
        positioning: OVERLAY_POSITIONING.BOTTOM_CENTER,
        position: coords[i],
      });
      this.segmentTooltips.push(segmentTooltip);
      map.addOverlay(segmentTooltip);
    }
  }

  /**
   * Add close button to tooltip
   */
  @action addTooltipClose() {
    const close = document.createElement('a');
    close.href = '';
    close.className = 'tooltip-close';
    close.innerText = 'x';
    close.onclick = (e) => {
      e.preventDefault();
      if (this.activeTool) {
        this.activeTool.disposer();
        this.activeTool.disposer = this.addInteraction(this.activeTool.type);
      }
    };
    this.measureTooltipElement.appendChild(close);
  }

  /**
   * Format LineString length
   *
   * @param {ol.geom.LineString} geom - Geometry to format length of
   */
  formatLength(geom: LineString, projection?: string) {
    let text = '';

    const length = getLength(geom, { projection });

    const distanceKm = Math.round((length / 1000) * 100) / 100;
    const distanceM = Math.round(length * 100) / 100;

    if (this.options.showProjMeasurement) {
      text = `${geom.getLength().toFixed(10)} `;
      if (length > 100) text += `(${distanceKm} км)`;
      else text += `(${distanceM} м)`;
    } else {
      if (length > 100) text += `${distanceKm} км`;
      else text += `${distanceM} м`;
    }

    return text;
  }

  /**
   * Format Polygon area
   *
   * @param {ol.geom.Polygon} geom - Geometry to format area of
   */
  formatArea(geom: Polygon, projection: string) {
    let text = '';

    const area = getArea(geom, { projection });

    const areaKm = Math.round((area / 1000000) * 100) / 100;
    const areaM = Math.round(area * 100) / 100;

    if (this.options.showProjMeasurement) {
      text = `${geom.getArea().toFixed(10)} `;
      if (area > 10000) text += `(${areaKm} км<sup>2</sup>)`;
      else text += `(${areaM} м<sup>2</sup>)`;
    } else {
      if (area > 10000) text += `${areaKm} км<sup>2</sup>`;
      else text += `${areaM} м<sup>2</sup>`;
    }

    return text;
  }
}
